import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import {
  RefreshCw, Pencil, ArrowRightLeft, Power,
  Circle, CheckCircle2, ChevronRight, ChevronDown, ChevronUp,
  Calendar, CalendarDays,
} from 'lucide-react';
import EnrollmentSheet from './EnrollmentSheet';
import ChangeProgramDialog from './ChangeProgramDialog';
import ProgramDeactivateDialog from './ProgramDeactivateDialog';
import ServiceOutlookCard from './ServiceOutlookCard';
import RescheduleJobModal from '../../../../components/scheduler/RescheduleJobModal';
import { formatEnrollmentProgress } from '../../../../lib/enrollmentProgress';
import { useCompany } from '../../../../context/CompanyContext';
import { useTasks } from '../../../../context/TasksContext';
import {
  NON_TERMINAL_RECURRING_STATUSES,
  toLocalDate,
  findEarliestLiveRecurringTaskForEnrollment,
} from '../../../../utils/buildForecast';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * EnrollmentCard — Phase 2 first-class card for a single enrollment.
 *
 * Reads all display state from the passed-in enrollment doc (Phase 2
 * authoritative source), never from `client.recurrence`. Exposes:
 *   - Edit          → open the sheet in `edit` mode → PATCH /enrollments/{eid}
 *   - Change Program → open the ChangeProgramDialog → prompts new-program
 *                      details + operator choice on future visits
 *   - Deactivate     → ProgramDeactivateDialog (same operator choice pattern
 *                      as legacy) → POST /enrollments/{eid}/end
 *
 * For ended/superseded enrollments the action row is hidden and the card
 * renders in a muted history style.
 */
const EnrollmentCard = ({ enrollment, client, programs, onMutated }) => {
  const { companyId } = useCompany();
  const { tasks } = useTasks();

  const [editing, setEditing] = useState(false);
  const [changing, setChanging] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [deactivateSubmitting, setDeactivateSubmitting] = useState(false);
  const [outlookOpen, setOutlookOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  const isActive = enrollment?.status === 'active';
  const isSuperseded = enrollment?.status === 'superseded';
  const isEnded = enrollment?.status === 'ended';

  const programTitle = enrollment?.programName || enrollment?.planLabel || 'Program';
  const completed = enrollment?.completedOccurrences ?? 0;
  const total = enrollment?.totalOccurrences ?? null;
  const isExhausted = total != null && completed >= total;

  // ── Sprint 309: enrollment-scoped forecast + reschedule ─────────────
  // Filter the tasks feed down to tasks that belong to THIS enrollment
  // (with a legacy fallback that only fires when this enrollment IS the
  // client's primary, i.e. `activeEnrollmentId` points at it). The
  // synthetic lead-view below carries the enrollment's cadence into the
  // existing `projectPlanWithDrift` pipeline via `recurrence.*`.
  const enrollmentScopedTasks = useMemo(() => {
    const eid = enrollment?.enrollmentId;
    if (!eid) return [];
    const isPrimary = client?.activeEnrollmentId === eid;
    return (tasks || []).filter((t) => {
      if (t.enrollmentId === eid) return true;
      if (t.recurringServiceId === eid) return true;
      if (isPrimary && client?.id && t.leadId === client.id
          && !t.enrollmentId && !t.recurringServiceId) return true;
      if (isPrimary && client?.id && t.recurringServiceId === client.id) return true;
      return false;
    });
  }, [tasks, enrollment?.enrollmentId, client?.id, client?.activeEnrollmentId]);

  const enrollmentLeadView = useMemo(() => {
    if (!enrollment) return null;
    return {
      // Preserve the real leadId so downstream filters/anchors work.
      id:        client?.id,
      firstName: client?.firstName,
      lastName:  client?.lastName,
      name:      client?.name,
      // Lifecycle passthrough (Inactive/Closed banners in the outlook card).
      accountStatus: client?.accountStatus,
      status:        client?.status,
      // Recurrence is derived from THIS enrollment — includes the Phase 1
      // legacy aliases so the forecast anchor priority
      // (nextJobDate → lastJobDate+interval → startDate → today) hits.
      recurrence: {
        active:               isActive,
        interval:             enrollment.interval,
        unit:                 enrollment.unit,
        rollMode:             enrollment.rollMode,
        completedOccurrences: enrollment.completedOccurrences ?? 0,
        totalOccurrences:     enrollment.totalOccurrences ?? null,
        planLabel:            enrollment.planLabel || enrollment.programName || 'Recurring Service',
        startDate:            enrollment.startDate,
        endDate:              enrollment.endDate,
        nextJobDate:          enrollment.nextServiceDate,
        lastJobDate:          enrollment.lastServiceDate,
        nextServiceDate:      enrollment.nextServiceDate,
        lastServiceDate:      enrollment.lastServiceDate,
      },
    };
  }, [enrollment, client?.id, client?.firstName, client?.lastName, client?.name,
       client?.accountStatus, client?.status, isActive]);

  const nextLiveTask = useMemo(
    () => findEarliestLiveRecurringTaskForEnrollment(
      enrollment, enrollmentScopedTasks,
      { leadId: client?.id, activeEnrollmentId: client?.activeEnrollmentId },
    ),
    [enrollment, enrollmentScopedTasks, client?.id, client?.activeEnrollmentId],
  );

  // Count active FUTURE tasks belonging to this enrollment — needed for
  // the deactivate dialog's "N visits will be cancelled" copy.
  const futureVisitCount = useMemo(() => {
    if (!isActive || !enrollment?.enrollmentId) return 0;
    const nowIso = new Date().toISOString();
    return enrollmentScopedTasks.filter((t) => {
      if (t.type !== 'job') return false;
      if (!NON_TERMINAL_RECURRING_STATUSES.has(t.status)) return false;
      return typeof t.scheduledStart === 'string' && t.scheduledStart >= nowIso;
    }).length;
  }, [enrollmentScopedTasks, enrollment?.enrollmentId, isActive]);

  const nextServiceDateStr = (
    (isActive && nextLiveTask?.scheduledStart)
    || enrollment?.nextServiceDate
    || enrollment?.startDate
    || null
  );
  const formatDate = (val) => {
    if (!val) return null;
    const d = toLocalDate(val);
    if (!d) return null;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const formatMoney = (v) => v == null ? null : Number(v).toLocaleString('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: v % 1 === 0 ? 0 : 2,
  });

  const cadenceLabel = (() => {
    const n = enrollment?.interval;
    if (!n) return null;
    // Normalize unit: legacy leads stored 'days' (plural); enrollments
    // store 'day'/'week'/'month' (singular). Strip trailing 's' before
    // re-pluralizing so we never render "dayss".
    const raw = (enrollment?.unit || 'day').toLowerCase().replace(/s$/, '');
    return `Every ${n} ${raw}${n === 1 ? '' : 's'}`;
  })();

  const statusPill = () => {
    if (isSuperseded) return (
      <span
        className="inline-flex items-center gap-1 text-[11px] font-medium bg-purple-50 text-purple-700 rounded-full px-2.5 py-0.5"
        data-testid={`enrollment-${enrollment.enrollmentId}-status-superseded`}
      >
        <ArrowRightLeft className="h-3 w-3" /> Superseded
      </span>
    );
    if (isEnded) return (
      <span
        className="inline-flex items-center gap-1 text-[11px] font-medium bg-slate-100 text-slate-600 rounded-full px-2.5 py-0.5"
        data-testid={`enrollment-${enrollment.enrollmentId}-status-ended`}
      >
        <CheckCircle2 className="h-3 w-3" /> Ended
      </span>
    );
    if (isExhausted) return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-gray-100 text-gray-500 rounded-full px-2.5 py-0.5">
        <CheckCircle2 className="h-3 w-3" /> Complete
      </span>
    );
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] font-medium bg-green-50 text-green-700 rounded-full px-2.5 py-0.5"
        data-testid={`enrollment-${enrollment.enrollmentId}-status-active`}
      >
        <Circle className="h-3 w-3 fill-green-500 text-green-500" /> Active
      </span>
    );
  };

  // ── Handlers ─────────────────────────────────────────────────────────
  const handleDeactivateConfirm = async (cancelFutureVisits) => {
    setDeactivateSubmitting(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const res = await axios.post(
        `${API}/api/leads/${client.id}/enrollments/${enrollment.enrollmentId}/end`,
        { cancelFutureVisits, reason: null },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      const cancelled = res?.data?.cancelledTaskCount ?? 0;
      if (cancelFutureVisits && cancelled > 0) {
        toast.success(`Program ended · ${cancelled} future visit${cancelled === 1 ? '' : 's'} cancelled`);
      } else {
        toast.success('Program ended · scheduled visits kept');
      }
      setDeactivating(false);
      onMutated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to deactivate program');
    } finally {
      setDeactivateSubmitting(false);
    }
  };

  // ── Edit sheet swap ──────────────────────────────────────────────────
  if (editing) {
    return (
      <EnrollmentSheet
        mode="edit"
        enrollment={enrollment}
        client={client}
        programs={programs}
        onClose={() => setEditing(false)}
        onSaved={() => { setEditing(false); onMutated?.(); }}
      />
    );
  }

  return (
    <>
      <ChangeProgramDialog
        isOpen={changing}
        currentEnrollment={enrollment}
        client={client}
        programs={programs}
        futureVisitCount={futureVisitCount}
        onClose={() => setChanging(false)}
        onChanged={() => { setChanging(false); onMutated?.(); }}
      />
      <ProgramDeactivateDialog
        isOpen={deactivating}
        futureVisitCount={futureVisitCount}
        submitting={deactivateSubmitting}
        onCancel={() => !deactivateSubmitting && setDeactivating(false)}
        onConfirm={handleDeactivateConfirm}
      />
      <RescheduleJobModal
        task={nextLiveTask}
        isOpen={rescheduleOpen && !!nextLiveTask}
        onClose={() => setRescheduleOpen(false)}
      />

      <div
        className={
          'rounded-md border p-3 '
          + (isActive
              ? 'bg-white border-gray-200'
              : 'bg-gray-50 border-gray-200 opacity-90')
        }
        data-testid={`enrollment-card-${enrollment.enrollmentId}`}
      >
        {/* Header: title + status + edit pencil */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <RefreshCw className={`h-4 w-4 shrink-0 ${isActive ? 'text-green-500' : 'text-gray-300'}`} />
            <h3
              className="text-sm font-semibold text-gray-900 truncate"
              data-testid={`enrollment-title-${enrollment.enrollmentId}`}
              title={programTitle}
            >
              {programTitle}
            </h3>
            {statusPill()}
          </div>
          {isActive && (
            <button
              onClick={() => setEditing(true)}
              className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
              data-testid={`enrollment-edit-${enrollment.enrollmentId}`}
              title="Edit program"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Facts grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          {cadenceLabel && (
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Cadence</p>
              <p className="text-sm font-medium text-gray-800"
                 data-testid={`enrollment-cadence-${enrollment.enrollmentId}`}>
                {cadenceLabel}
              </p>
            </div>
          )}
          {enrollment?.pricingAmount != null && (
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Price / Visit</p>
              <p className="text-sm font-medium text-gray-800"
                 data-testid={`enrollment-price-${enrollment.enrollmentId}`}>
                {formatMoney(enrollment.pricingAmount)}
              </p>
            </div>
          )}
          <div>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">
              {isActive ? 'Next Service' : (isSuperseded ? 'Ended On' : 'Ended On')}
            </p>
            <p className="text-sm font-medium text-gray-800"
               data-testid={`enrollment-next-${enrollment.enrollmentId}`}>
              {isActive
                ? (formatDate(nextServiceDateStr) || 'Not scheduled')
                : (formatDate(enrollment?.endDate) || '—')}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">
              {total != null ? 'Progress' : 'Visits'}
            </p>
            <p className="text-sm font-medium text-gray-800"
               data-testid={`enrollment-progress-${enrollment.enrollmentId}`}>
              {formatEnrollmentProgress(completed, total)}
            </p>
          </div>
        </div>

        {/* Actions row (active only) */}
        {isActive && (
          <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100 flex-wrap">
            <button
              type="button"
              onClick={() => setChanging(true)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-50 transition-colors"
              data-testid={`enrollment-change-btn-${enrollment.enrollmentId}`}
            >
              <ArrowRightLeft className="h-3 w-3" />
              Change Program
            </button>
            <button
              type="button"
              onClick={() => setRescheduleOpen(true)}
              disabled={!nextLiveTask}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid={`enrollment-reschedule-btn-${enrollment.enrollmentId}`}
              title={nextLiveTask ? 'Reschedule the next visit for this program' : 'No live upcoming visit to reschedule'}
            >
              <Calendar className="h-3 w-3" />
              Reschedule next
            </button>
            <button
              type="button"
              onClick={() => setDeactivating(true)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-rose-200 bg-white text-rose-700 hover:bg-rose-50 transition-colors ml-auto"
              data-testid={`enrollment-deactivate-btn-${enrollment.enrollmentId}`}
            >
              <Power className="h-3 w-3" />
              Deactivate
            </button>
          </div>
        )}

        {/* ── Upcoming Visits (Service Outlook, enrollment-scoped) ─────── */}
        {isActive && enrollmentLeadView && (
          <div className="mt-3 pt-3 border-t border-gray-100" data-testid={`enrollment-outlook-${enrollment.enrollmentId}`}>
            <button
              type="button"
              onClick={() => setOutlookOpen((v) => !v)}
              className="w-full flex items-center justify-between gap-2 text-left"
              data-testid={`enrollment-outlook-toggle-${enrollment.enrollmentId}`}
              aria-expanded={outlookOpen}
            >
              <span className="text-xs font-semibold text-gray-700 inline-flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5 text-gray-500" />
                Upcoming Visits
              </span>
              {outlookOpen
                ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" />
                : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
            </button>
            {outlookOpen && (
              <div className="mt-3">
                <ServiceOutlookCard
                  client={enrollmentLeadView}
                  embedded={true}
                  tasksOverride={enrollmentScopedTasks}
                />
              </div>
            )}
          </div>
        )}

        {/* Supersede link (history) */}
        {isSuperseded && enrollment.supersededByEnrollmentId && (
          <div className="mt-3 pt-3 border-t border-gray-100 text-[11px] text-gray-500 flex items-center gap-1">
            <ChevronRight className="h-3 w-3" />
            Superseded by a newer program
            {enrollment.endReason && (
              <span className="italic ml-1">— {enrollment.endReason}</span>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default EnrollmentCard;
